import React, { forwardRef } from 'react';

// Wide enough to fit a full 10-char Wellis ID (e.g. "KENDRICK02") without
// truncating, at every zoom level -- width no longer matches height, since
// these render as pills, not circles.
const getNodeSize = (zoomLevel) => {
  switch (zoomLevel) {
    case 'zoomed_out':
      return { width: 44, height: 20, padding: 2, fontSize: 6 };
    case 'medium':
      return { width: 64, height: 28, padding: 3, fontSize: 7 };
    case 'close':
      return { width: 88, height: 36, padding: 4, fontSize: 8 };
    default:
      return { width: 110, height: 44, padding: 6, fontSize: 9 };
  }
};

const truncateWellisId = (code) => {
  if (!code) return '';
  return code.replace(/^WELLIS-/, '');
};

const getZoomLevel = (zoom) => {
  if (zoom < 0.5) return 'zoomed_out';
  if (zoom < 1) return 'medium';
  if (zoom < 1.5) return 'close';
  return 'full';
};

function TreeNodeComponent({
  node,
  zoom,
  isCollapsed,
  onCollapse,
  isSelected,
  onSelect,
  isLoading,
}, ref) {
  const zoomLevel = getZoomLevel(zoom);
  const size = getNodeSize(zoomLevel);

  const getInitials = (name, email) => {
    if (name) {
      const parts = name.split(' ');
      return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
    }
    return email ? email[0].toUpperCase() : '?';
  };

  // ID only, bold -- no name, no leg/level line, at any zoom level (per
  // explicit product decision: the Wellis ID alone is what's wanted here).
  const truncatedId = truncateWellisId(node.affiliate_code);
  const content = (
    <div className="text-center w-full overflow-hidden leading-tight">
      <div className="font-bold truncate leading-tight" style={{ fontSize: `${size.fontSize}px` }}>
        {truncatedId || getInitials(node.user_name, node.user_email)}
      </div>
    </div>
  );

  const handleClick = (e) => {
    e.stopPropagation();
    onSelect?.(node.node_id);
  };

  const handleCollapse = (e) => {
    e.stopPropagation();
    onCollapse(node.node_id);
  };

  const nodeStyle = {
    width: `${size.width}px`,
    height: `${size.height}px`,
    minWidth: `${size.width}px`,
    minHeight: `${size.height}px`,
  };

  // Backfill Bonus accounts (a real, already-placed user whose own future
  // bonus earnings are still paying off the package a superadmin backfilled
  // for them) render as an outlined/transparent node with a permanent gold
  // ring and gold text, instead of the normal solid-gold fill -- visually
  // signals "filled slot, not yet self-earned" without being a real
  // EmptyNode (which has no underlying user at all).
  const isBackfilled = !!node.is_backfilled_account;

  return (
    <div
      ref={ref}
      data-tree-node
      className={`absolute flex items-center justify-center rounded-lg shadow transition-all cursor-pointer transform hover:scale-110 ${
        isCollapsed ? 'opacity-75' : ''
      }`}
      style={{
        ...nodeStyle,
        fontSize: `${size.fontSize}px`,
        padding: `${size.padding}px`,
        backgroundColor: isBackfilled ? 'transparent' : '#c9a961',
        color: isBackfilled ? '#c9a961' : 'black',
        border: isBackfilled ? '2px solid #c9a961' : (isSelected ? '2px solid #c9a961' : undefined),
        boxShadow: isSelected && !isBackfilled ? '0 0 0 2px #c9a961' : undefined,
      }}
      onClick={handleClick}
      title={`${node.user_name || node.user_email}\n${node.affiliate_code}${isBackfilled ? '\n(Backfill Bonus)' : ''}\nL: ${Number(node.leg1_volume || 0).toFixed(2)}  M: ${Number(node.leg2_volume || 0).toFixed(2)}  R: ${Number(node.leg3_volume || 0).toFixed(2)}`}
    >
      <div className={`flex flex-col items-center justify-center w-full h-full ${isBackfilled ? '' : 'text-black'}`} style={isBackfilled ? { color: '#c9a961' } : undefined}>
        {content}
      </div>
      {node.children && node.children.length > 0 && (
        <button
          className={`absolute top-1 right-1 w-4 h-4 rounded-full text-[8px] flex items-center justify-center ${
            isLoading ? 'animate-spin' : ''
          }`}
          style={isBackfilled
            ? { backgroundColor: 'rgba(201, 169, 97, 0.3)', color: '#c9a961' }
            : { backgroundColor: 'rgba(0, 0, 0, 0.3)', color: 'black' }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isBackfilled ? 'rgba(201, 169, 97, 0.5)' : 'rgba(0, 0, 0, 0.5)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isBackfilled ? 'rgba(201, 169, 97, 0.3)' : 'rgba(0, 0, 0, 0.3)'}
          onClick={handleCollapse}
          disabled={isLoading}
          title={isCollapsed ? 'Expand' : 'Collapse'}
        >
          {isLoading ? '⟳' : isCollapsed ? '+' : '−'}
        </button>
      )}
    </div>
  );
}

export default forwardRef(TreeNodeComponent);
